import { useCallback, useRef } from 'react';
import { NarrativeRowNavProps } from '../use-narrative-keyboard-nav/use-narrative-keyboard-nav';
import { getMenuButtons } from '../../components/narrative-action-menu';

export interface UseNarrativeRowKeyboardOptions {
    rowNavProps?: NarrativeRowNavProps;
    handleRowDeselect: () => void;
    handleRowFocus: () => void;
    handleRowBlur: (event: { relatedTarget: EventTarget | null }) => void;
    /** Returns focus to the compose input — used only by Escape (deselect +
     *  return to input). Unrelated to returnFocusToRow (edit/delete-cancel's
     *  "stay put" focus target, threaded separately into
     *  useNarrativeRowActions via rowNavProps.focusSelf). */
    focusNarrativeInput?: () => void;
}

export interface UseNarrativeRowKeyboardResult {
    /** Deselects the row and returns focus to the compose input. Passed to
     *  NarrativeActionMenu's onEscape (Escape from within the menu), and
     *  reused by handleRowKeyDown for Escape pressed on the row itself. */
    handleMenuEscape: () => void;
    /** Wire onto the row's onKeyDown. Escape deselects + refocuses the
     *  input. Left/Right or Tab/Shift+Tab — only when focus is on the row
     *  itself (not yet on one of its own menu buttons) AND the row wasn't
     *  JUST reached by backing out of its own menu — enters the action menu
     *  (first button on Right/Tab, last on Left/Shift+Tab). Once focus is
     *  already on a menu button this is a no-op here, since
     *  NarrativeActionMenu's own keydown handler (closer ancestor, fires
     *  first in the bubble phase) already moved it, or native Tab order
     *  takes over. The Tab/Shift+Tab handling exists because the menu is
     *  display:none until the row is selected/hovered (rule 3) — selecting
     *  the row is itself a side effect of the row receiving focus, so a
     *  bare Shift+Tab from outside the list would otherwise land on the row
     *  and then skip straight past its (now newly-visible) menu on the next
     *  press, since the browser already computed "previous focusable
     *  element" before React revealed it. Redirecting Tab/Shift+Tab the same
     *  way as Right/Left makes both directions reach a button in exactly 2
     *  presses from outside the list. The "just backed out of the menu"
     *  exclusion (see handleFocus) is what stops that from looping forever:
     *  without it, Shift+Tab from the menu's first button natively lands
     *  back on the row, and this handler would re-enter at the last button
     *  every time, so Shift+Tab could never actually leave the row/menu
     *  pair. Everything else (Up/Down) delegates to rowNavProps. */
    handleRowKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    /** Wire onto the row's onFocus. Combines the keyboard-nav roving-tabindex
     *  bookkeeping (rowNavProps) with recording this row as selected, and
     *  records (via relatedTarget) whether this focus arrived from one of
     *  the row's own menu buttons — see handleRowKeyDown. */
    handleFocus: (event: React.FocusEvent<HTMLElement>) => void;
    /** Wire onto the row's onBlur. Ignores focus moving to one of this row's
     *  own descendants (its action-menu buttons) — only a move outside the
     *  row is a real deselect; otherwise the CSS-only .is-selected "selected"
     *  look would flicker off mid-interaction with the row's own menu. */
    handleBlur: (event: React.FocusEvent<HTMLElement>) => void;
}

/**
 * Shared keyboard/focus wiring for the two narrative rows (chronological and
 * pinned). Composes the raw selection/action primitives from
 * useNarrativeRowActions (handleRowFocus, handleRowBlur, handleRowDeselect)
 * with rowNavProps into the handlers a row wires onto its JSX.
 *
 * Kept as its own hook rather than folded into useNarrativeRowActions: that
 * hook's primitives are plain store operations, testable by calling them
 * directly with fabricated event shapes — no real DOM needed. This hook is
 * the DOM-event layer on top (real KeyboardEvent/FocusEvent objects,
 * document.activeElement, querySelector), which needs actual DOM nodes to
 * test meaningfully. Splitting them keeps that distinction visible instead
 * of mixing "pure store logic" and "real DOM interaction" tests in one file.
 */
export function useNarrativeRowKeyboard({
    rowNavProps,
    handleRowDeselect,
    handleRowFocus,
    handleRowBlur,
    focusNarrativeInput,
}: UseNarrativeRowKeyboardOptions): UseNarrativeRowKeyboardResult {
    const handleMenuEscape = useCallback(() => {
        handleRowDeselect();
        focusNarrativeInput?.();
    }, [handleRowDeselect, focusNarrativeInput]);

    // True only for the one focus event that lands on the row by backing out
    // of its own menu via native Shift+Tab from the first button (arrow-key
    // navigation never returns to the row this way — see handleRowKeyDown's
    // doc comment). Recomputed on every focus event, so it never goes stale
    // across repeated enter/exit cycles.
    const cameFromOwnMenuRef = useRef(false);

    const handleRowKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                handleMenuEscape();
                return;
            }
            const isTab = event.key === 'Tab' && !cameFromOwnMenuRef.current;
            const entersMenuForward = event.key === 'ArrowRight' || (isTab && !event.shiftKey);
            const entersMenuBackward = event.key === 'ArrowLeft' || (isTab && event.shiftKey);
            if (
                (entersMenuForward || entersMenuBackward) &&
                document.activeElement === event.currentTarget
            ) {
                const menuButtons = getMenuButtons(
                    event.currentTarget.querySelector('.narrative-action-menu')
                );
                const target = entersMenuForward
                    ? menuButtons[0]
                    : menuButtons[menuButtons.length - 1];
                if (target != null) {
                    event.preventDefault();
                    target.focus();
                }
                return;
            }
            rowNavProps?.onKeyDown?.(event);
        },
        [rowNavProps, handleMenuEscape]
    );

    const handleFocus = useCallback(
        (event: React.FocusEvent<HTMLElement>) => {
            const relatedTarget = event.relatedTarget as Node | null;
            cameFromOwnMenuRef.current =
                relatedTarget !== null && event.currentTarget.contains(relatedTarget);
            rowNavProps?.onFocus?.();
            handleRowFocus();
        },
        [rowNavProps, handleRowFocus]
    );

    const handleBlur = useCallback(
        (event: React.FocusEvent<HTMLElement>) => {
            const relatedTarget = event.relatedTarget as Node | null;
            if (relatedTarget !== null && event.currentTarget.contains(relatedTarget)) {
                return;
            }
            handleRowBlur({ relatedTarget: event.relatedTarget });
        },
        [handleRowBlur]
    );

    return { handleMenuEscape, handleRowKeyDown, handleFocus, handleBlur };
}
